import { useRef, useCallback, useState } from 'react';
import { log } from '../lib/log';

interface UseAudioPlayerProps {
  isConnected?: boolean;
  onInitialize?: () => void;
  onDispose?: () => void;
}

function useAudioPlayer({
  isConnected,
  onInitialize,
  onDispose,
}: UseAudioPlayerProps) {
  const audioQueueRef = useRef<string[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const currentSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const isPlayingRef = useRef<boolean>(false);
  const mutedRef = useRef<boolean>(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const initializeAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext ||
        (window as any).webkitAudioContext)({
        sampleRate: 44100, // Web Audio API often works best with standard rates
      });

      gainNodeRef.current = audioContextRef.current.createGain();
      gainNodeRef.current.gain.setValueAtTime(
        1,
        audioContextRef.current.currentTime
      );
      gainNodeRef.current.connect(audioContextRef.current.destination);

      console.log(
        'AudioContext initialized with sample rate:',
        audioContextRef.current.sampleRate
      );
    }
  }, []);

  const clearBuffer = useCallback(() => {
    audioQueueRef.current = [];
  }, []);

  const stop = useCallback(() => {
    if (
      isPlayingRef.current &&
      audioContextRef.current &&
      currentSourceRef.current
    ) {
      currentSourceRef.current.stop();
      isPlayingRef.current = false;
      setIsPlaying(false);
      currentSourceRef.current = null;
      clearBuffer();
    } else {
      clearBuffer(); // If not playing, just clear the buffer
    }
  }, [clearBuffer]);

  const queueBuffer = useCallback((base64Audio: string) => {
    if (audioQueueRef.current) {
      audioQueueRef.current.push(base64Audio);
      if (!isPlayingRef.current) {
        processAudioQueue();
      }
    }
  }, []);

  const connect = useCallback(() => {
    initializeAudioContext();
    onInitialize?.();
  }, [initializeAudioContext]);

  const disconnect = useCallback(() => {
    if (isConnected) {
      // We don't have direct access to the WebSocket instance here,
      // so the component using the hook might need to manage the WebSocket object
      stop();
      clearBuffer();
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      onDispose?.();
    }
  }, [isConnected, stop, clearBuffer]);

  const mute = useCallback(() => {
    if (currentSourceRef.current) {
      if (gainNodeRef.current && audioContextRef.current) {
        gainNodeRef.current.gain.setValueAtTime(
          0,
          audioContextRef.current.currentTime
        );
      }
      mutedRef.current = true;
    }
  }, []);

  const unmute = useCallback(() => {
    if (currentSourceRef.current && audioContextRef.current?.destination) {
      if (gainNodeRef.current && audioContextRef.current) {
        gainNodeRef.current.gain.setValueAtTime(
          1,
          audioContextRef.current.currentTime
        );
      }
      mutedRef.current = false;
    }
  }, []);

  const processAudioQueue = useCallback(() => {
    if (
      !audioContextRef.current ||
      isPlayingRef.current ||
      audioQueueRef.current?.length === 0
    ) {
      return;
    }

    isPlayingRef.current = true;
    setIsPlaying(true);
    const base64Audio = audioQueueRef.current?.shift();

    if (base64Audio) {
      const binaryString = atob(base64Audio);
      const byteArray = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        byteArray[i] = binaryString.charCodeAt(i);
      }
      log(
        'info',
        'processAudioQueue::byteArray::length',
        byteArray.length,
        'with mutedRef.current set to',
        mutedRef.current
      );

      audioContextRef.current
        .decodeAudioData(byteArray.buffer)
        .then((audioBuffer) => {
          if (!audioContextRef.current || !gainNodeRef.current) {
            console.warn('Audio context or gain node lost during decoding');
            isPlayingRef.current = false;
            setIsPlaying(false);
            return;
          }

          const source = audioContextRef.current!.createBufferSource();
          currentSourceRef.current = source;
          source.buffer = audioBuffer;
          source.connect(gainNodeRef.current);
          source.onended = () => {
            isPlayingRef.current = false;
            currentSourceRef.current = null;
            setIsPlaying(false);
            processAudioQueue(); // Process the next buffer
          };
          source.start();
          isPlayingRef.current = true;
          setIsPlaying(true);
        })
        .catch((error) => {
          console.error('Error decoding audio data:', error);
          isPlayingRef.current = false;
          setIsPlaying(false);
          currentSourceRef.current = null;
          processAudioQueue(); // Try the next buffer
        });
    } else {
      isPlayingRef.current = false;
      setIsPlaying(false);
      currentSourceRef.current = null;
    }
  }, []);

  const play = useCallback(() => {
    if (
      !isPlayingRef.current &&
      audioQueueRef.current &&
      audioQueueRef.current.length > 0
    ) {
      processAudioQueue();
    }
  }, [processAudioQueue]);

  const pause = useCallback(() => {
    if (
      isPlayingRef.current &&
      audioContextRef.current &&
      currentSourceRef.current
    ) {
      audioContextRef.current.suspend();
      isPlayingRef.current = false;
      setIsPlaying(false);
    }
  }, []);

  console.log('isPlaying ::: ', isPlaying);
  return {
    isConnected,
    queueLength: audioQueueRef.current?.length ?? 0,
    connect,
    disconnect,
    play,
    pause,
    stop,
    queueBuffer,
    clearBuffer,
    mute,
    unmute,
    isPlaying,
  };
}

export default useAudioPlayer;
